import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { catchError, Observable, throwError } from "rxjs";
import { BulkValidateRequest, ContractDocument, ContractFieldOptions, ContractFilterRequest, ContractPage, ContractPageRequest, ContractSyncResult, ValidateContractFieldRequest, ValidateFieldRequest } from "./interfaces/contractdocument";
import { environment } from "src/environments/environment";
import { Utils } from "src/app/utils/utils";

@Injectable({ providedIn: "root" })
export class ContractValidationService {
    private readonly apiUrl: string;

    constructor(private readonly httpClient: HttpClient) {
        this.apiUrl = `${environment.endpoint}api/ContractValidation`;
    }

    getAllContracts(): Observable<ContractDocument[]> {
        return this.httpClient.get<ContractDocument[]>(`${this.apiUrl}/contracts`).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts`, error);
                return throwError(() => error);
            })
        );
    }

    getFilteredContracts(filter: ContractFilterRequest): Observable<ContractDocument[]> {
        return this.httpClient.post<ContractDocument[]>(`${this.apiUrl}/contracts/filter`, filter).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/filter`, error);
                return throwError(() => error);
            })
        );
    }

    getContractsPage(request: ContractPageRequest): Observable<ContractPage> {
        return this.httpClient.post<ContractPage>(`${this.apiUrl}/contracts/page`, request).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/page`, error);
                return throwError(() => error);
            })
        );
    }

    getContractFieldOptions(): Observable<ContractFieldOptions> {
        return this.httpClient.get<ContractFieldOptions>(`${this.apiUrl}/contracts/field-options`).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/field-options`, error);
                return throwError(() => error);
            })
        );
    }

    getContractById(id: number): Observable<ContractDocument> {
        return this.httpClient.get<ContractDocument>(`${this.apiUrl}/contracts/${id}`).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/${id}`, error);
                return throwError(() => error);
            })
        );
    }

    getContractByDocumentId(documentId: string): Observable<ContractDocument> {
        return this.httpClient.get<ContractDocument>(`${this.apiUrl}/contracts/by-document/${documentId}`).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/by-document/${documentId}`, error);
                return throwError(() => error);
            })
        );
    }

    getPdfContractById(contractId: string): Observable<Blob> {
        return this.httpClient.get(`${this.apiUrl}/pdf/${contractId}`, { responseType: 'blob' }).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/pdf/${contractId}`, error);
                return throwError(() => error);
            })
        );
    }

    getPdfContractByDocumentId(documentId: string): Observable<Blob> {
        return this.httpClient.get(`${this.apiUrl}/pdf/by-document/${documentId}`, { responseType: 'blob' }).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/pdf/by-document/${documentId}`, error);
                return throwError(() => error);
            })
        );
    }

    createContract(contract: ContractDocument): Observable<ContractDocument> {
        return this.httpClient.post<ContractDocument>(`${this.apiUrl}/contracts`, contract).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts`, error);
                return throwError(() => error);
            })
        );
    }

    saveContract(id: number): Observable<void> {
        return this.httpClient.post<void>(`${this.apiUrl}/contracts/${id}/save`, null).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/${id}/save`, error);
                return throwError(() => error);
            })
        );
    }

    deleteContract(id: number): Observable<any> {
        return this.httpClient.delete(`${this.apiUrl}/contracts/${id}`).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/contracts/${id}`, error);
                return throwError(() => error);
            })
        );
    }

    validateField(request: ValidateFieldRequest): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/validate-field`, request).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/validate-field`, error);
                return throwError(() => error);
            })
        );
    }

    validateContractField(request: ValidateContractFieldRequest): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/validate-contract-field`, request).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/validate-contract-field`, error);
                return throwError(() => error);
            })
        );
    }

    bulkValidate(request: BulkValidateRequest): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/bulk-validate`, request).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/bulk-validate`, error);
                return throwError(() => error);
            })
        );
    }

    /** Syncs one page of the CDP validation list. The caller repeats with the next page while hasNextPage is true. */
    synchronizeBatch(page: number, pageSize?: number): Observable<ContractSyncResult> {
        return this.httpClient.post<ContractSyncResult>(`${this.apiUrl}/sync`, { page, pageSize }).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/sync`, error);
                return throwError(() => error);
            })
        );
    }

    /** Removes one bounded page of stale local contracts using IDs carried by the caller. */
    removeStaleContractsBatch(
        pageSize: number,
        seenDocumentIds: string[],
        staleRemovalCursor: number
    ): Observable<ContractSyncResult> {
        return this.httpClient.post<ContractSyncResult>(`${this.apiUrl}/sync`, {
            removeStaleContracts: true,
            pageSize,
            seenDocumentIds,
            staleRemovalCursor
        }).pipe(
            catchError(error => {
                Utils.writeLog(this.httpClient, `${this.apiUrl}/sync`, error);
                return throwError(() => error);
            })
        );
    }
}
